<template lang="pug">
    multiplayer-mode(
        v-if="multiplayerMode"
        :player-name="playerName"
        :score="score"
        :time-with-hours="timeWithHours"
        :players-number="playersNumber"
        :place="place"
        :colorForStudent="colorForStudent"
        :display-game-info="displayGameInfo"
        @openModalFrame="$emit('openModalFrame')"
        :is-game-paused="isGamePaused"
        :slug="slug"
        @parametersUpdate="$emit('parametersUpdate')"
    )
    .game-info(v-else :class="{'game-info_top-centered': isX2}")
        .game-info__block
            .game-info__item.game-info__scores(
                v-if="score && !isOlympiad",
                :class="!isFromBattle && 'game-info__scores--blue'"
            )
                transition(name="left-score" mode="out-in")
                    span.left-score(:key="score.left", :class="{'playbox-score': !isFromBattle}") {{ score.left }}
                span.colon {{ ":" }}
                transition(name="right-score" mode="out-in")
                    span.right-score(:key="score.right", :class="{'playbox-score': !isFromBattle}") {{ score.right }}
            .game-info__item.game-info__time(v-if="time" :class="{'game-info__time_spaced': isX2}")
                .time__speed-up(v-if="isSpeedUpCircleShown")
                    span {{ currentSpeedUpValue }}
                img.time__icon(src="../../../assets/images/trainer/time.svg")
                span.time__value {{ time }}
            .game-info__item.game-info__speed(v-if="speed")
                i.fa.fa-fast-forward.speed__icon
                span.speed__value {{ speed }} {{ $i18n.t('trainer.trainer_gameFormSettingValue_seconds') }}
            .game-info__item.game-info__mode(v-if="mode && !isX2")
                svg-icon.game-mode__icon(v-if="mode === 'На счетах'", name="arithmetic", size="big")
                svg-icon.game-mode__icon(v-else-if="mode === 'В уме'", name="cloud", size="big")
                span.mode__value {{ modeName }}
            tip-button.tip-button(
                v-if="gameAllowsTips && tipsForCurrentParameters.length",
                :arrow-icon-rotated="showTip"
                @toggle-show-tip-variable="toggleShowTipVariable"
            )
            description-button.tip-button(
                v-if="gameAllowsDescription && descriptionForCurrentGame.length",
                :arrow-icon-rotated="showDescription"
                @toggle-show-tip-variable="toggleShowDescriptionVariable"
            )
            .abacus-button(
                @click="changeShowAbacusVariable",
                v-if="!isBattle && !isOlympiad && isActiveAbacusButton && !isX2",
            )
                svg-icon.abacus__arrow-icon(
                    name="angle-left",
                    size="small",
                    :class="{'abacus__arrow-icon--rotated': showAbacus}"
                )
                svg-icon.abacus__icon(name="abacus", size="big")
                span.abacus__value {{ $i18n.t('trainer.trainer_main_abacus') }}
            .opponent(v-if="isFromBattle && opponent")
                .opponent__info
                    span.opponent__name {{ opponentInfoText }}
                    img.opponent__avatar(:src="opponent.avatar")
                .opponent__example
                    .opponent__example_answering(v-if="isOpponentAnswering")
                        span {{ $i18n.t('battle.battle_playBox_opponentAnswering') }}
                        img(src="../../../assets/images/battles/game/pencil.png")
                    .opponent__example-abacus(v-else-if="parameters && parameters.game === 'InteractiveFlashCards'")
                        flash-cards-abacus(
                            :number="opponentNumbers[0]",
                            :is-static="true",
                            :is-battle-opponent="true",
                            :game-settings="{style: {backgroundColor: '#519FFC'}}",
                        )
                    .opponent__example-number(v-else-if="showedActions === null", v-for="number in opponentNumbers")
                        span(v-if="number > 0") &plus;
                        span(v-else) &minus;
                        | {{ parameters.fractions ? Math.abs(number).toFixed(parameters.fractionalPart) : Math.abs(number) }}
                    .opponent__example-number(v-else-if="!!opponentNumbers[showedActions]")
                        span(v-if="opponentNumbers[showedActions] > 0") &plus;
                        span(v-else) &minus;
                        | {{ parameters.fractions ? Math.abs(opponentNumbers[showedActions]).toFixed(parameters.fractionalPart) : Math.abs(opponentNumbers[showedActions]) }}
                    template(v-if="isX2 && showedActions !== null && !!opponentNumbers[showedActions]")
                        .opponent__example-divider
                        .opponent__example-number
                            span(v-if="opponentNumbers[showedActions] > 0") &plus;
                            span(v-else) &minus;
                            | {{ parameters.fractions ? Math.abs(opponentNumbers[showedActions]).toFixed(parameters.fractionalPart) : Math.abs(opponentNumbers[showedActions]) }}
                    .opponent__tables-container(
                        v-if="opponentTables.flat(Infinity).length  && parameters.slug === 'Schulte'",
                        v-for="table in opponentTables"
                    )
                        .opponent__example-table(v-if="table.length")
                            .example-table__row(v-for="row in table")
                                .example-table__cell(
                                    v-for="cell in row",
                                    :class="tableCellsClass"
                                )
                                    schulte-abacus(
                                        v-if='parameters.flashCards',
                                        :number='cell.number',
                                        :max-number='table.length ** 2',
                                        :digits="1",
                                        :is-battle-opponent="true",
                                    )
                                    .cell-value(
                                        v-else,
                                        :style="{'color': cell.color}",
                                        :class="{'cell-value_huge-table': table.length > 3}"
                                    ) {{ cell.number }}
        div(v-if="showTip")
            draggable(:start-position="tipsBlockInitialPosition")
                tips.tips(
                    :tips="tipsForCurrentParameters",
                    @toggle-show-tip-variable="toggleShowTipVariable"
                )
        div(v-if="showDescription")
            draggable(:start-position="tipsBlockInitialPosition")
                description.tips(
                    :tips="descriptionForCurrentGame",
                    @toggle-show-tip-variable="toggleShowDescriptionVariable"
                )
        portal(to="abacusDestination" :disabled="!abacusToPortal")
            div(v-if="showAbacus", :class="abacusFieldClass")
                abacus(
                    :game-difficult="gameDifficult",
                    :fractions="parameters.fractions",
                    :fractional-part="parameters.fractionalPart",
                    :number="currentAbacusNumber",
                    :digit-capacity="digitCapacity",
                    @number="changeAbacusNumber",
                    @resetAbacusNumber="resetAbacusNumber",
                )
</template>

<script>
    import Abacus from './addition-and-subtraction/tools/Abacus.vue';
    import Tips from './Tips.vue';
    import SchulteAbacus from '../Abacus/Abacus.vue';
    import FlashCardsAbacus from '../../tools/abacus/Abacus.vue';
    import MultiplayerMode from './MultiplayerMode.vue';
    import TipButton from './TipButton.vue';
    import { flatten } from 'lodash';
    import getTipsForCurrentParameters from '../../../utils/trainer/getTipsForCurrentParameters';
    import getDescriptionForCurrentGame from '../../../utils/trainer/getDescriptionForCurrentGame';
    import DescriptionButton from "./DescriptionButton.vue";
    import Description from "./Description.vue";

    export default {
        props: {
            score: {
                type: Object,
                default: null,
            },
            time: {
                type: String,
                default: null,
            },
            timeWithHours: {
                type: String,
                default: '',
            },
            isFromBattle: {
                type: Boolean,
                default: false,
            },
            isOlympiad: {
                type: Boolean,
                default: false,
            },
            mode: {
                type: String,
                default: '',
            },
            gameDifficult: {
                type: [String, Number],
                default: '',
            },
            isX2: {
                type: Boolean,
                default: false,
            },
            parameters: {
                type: Object,
                default: null,
            },
            initialSpeed: {
                type: [Number, String],
                default: 0,
            },
            opponent: {
                type: Object,
                default: null,
            },
            isOpponentAnswering: {
                type: Boolean,
                default: false,
            },
            opponentNumbers: {
                type: Array,
                default() {
                    return [];
                },
            },
            showedActions: {
                type: Number,
                default: null,
            },
            opponentTables: {
                type: Array,
                default() {
                    return [[[]]];
                },
            },
            multiplayerMode: {
                type: Boolean,
                default: false,
            },
            playerName: {
                type: String,
                default: '',
            },
            playersNumber: {
                type: Number,
                default: 0,
            },
            place: {
                type: Number,
                default: 0,
            },
            colorForStudent: {
                type: String,
                default: '',
            },
            displayGameInfo: {
                type: Boolean,
                default: false,
            },
            isGamePaused: {
                type: Boolean,
                default: false,
            },
            slug: {
                type: String,
                default: '',
            },
            speed: {
                type: [Boolean, Number],
                default: false,
            },
            digitCapacity: {
                type: Object,
                default: () => {},
            },
            abacusToPortal: {
                type: Boolean,
                default: false,
            },
            gameAllowsTips: {
                type: Boolean,
                default: false,
            },
            gameAllowsDescription: {
                type: Boolean,
                default: false,
            },
            isNeedToUseDefaultPositionForTipsBlock: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                showAbacus: false,
                showTip: false,
                showDescription: false,
                currentAbacusNumber: 0,
                isBattle: false,
                tipsForCurrentParameters: null,
                descriptionForCurrentGame: null,
                tipsBlockInitialPosition: null,
            };
        },
        mounted() {
            this.setTipsBlockInitialPosition();
        },
        methods: {
            setTipsBlockInitialPosition() {
                this.$nextTick(() => {
                    const gameInfoElement = document.querySelector('.game-info');
                    const gameInfoBoundingRect = gameInfoElement.getBoundingClientRect();
                    this.tipsBlockInitialPosition = this.isNeedToUseDefaultPositionForTipsBlock
                        ? {
                              bottom: '20px',
                              right: '20px',
                          }
                        : {
                              top: gameInfoBoundingRect.bottom + 20 + 'px',
                              right: window.innerWidth - gameInfoBoundingRect.right + 'px',
                          };
                });
            },
            changeAbacusNumber(val) {
                this.currentAbacusNumber = val;
            },
            resetAbacusNumber() {
                this.currentAbacusNumber = 0;
            },
            changeShowAbacusVariable() {
                this.showAbacus = !this.showAbacus;
                this.$emit('showAbacus', this.showAbacus);
            },
            toggleShowTipVariable() {
                this.showTip = !this.showTip;
            },
            toggleShowDescriptionVariable() {
                this.showDescription = !this.showDescription;
            },
        },
        watch: {
            parameters: {
                handler() {
                    this.tipsForCurrentParameters = getTipsForCurrentParameters(
                        _.cloneDeep(this.parameters),
                        this.slug
                    );
                    this.descriptionForCurrentGame = getDescriptionForCurrentGame(
                        _.cloneDeep(this.parameters),
                        this.slug
                    )
                },
                deep: true,
                immediate: true,
            },
        },
        computed: {
            isSpeedUpCircleShown() {
                return (
                    this.parameters && this.parameters.isSpeedIncrease && this.initialSpeed - this.parameters.speed > 0
                );
            },
            currentSpeedUpValue() {
                return (
                    Math.ceil((this.initialSpeed - this.parameters.speed) / this.parameters.speedUp) *
                    this.parameters.speedUp
                ).toFixed(1);
            },
            isActiveAbacusButton() {
                const gameRoutesForAbacucButton = [
                    'addition-and-subtraction:start',
                    'addition-and-subtraction-with-time:start',
                    'anzan:start',
                    'anzan-with-time:start',
                    'multiplication-and-division:start',
                    'roots-and-degrees:start',
                    'expressions:start',
                ];
                return gameRoutesForAbacucButton.includes(this.$route.name);
            },
            abacusFieldClass() {
                const gameWithTimeRoutes = ['addition-and-subtraction-with-time:start', 'anzan-with-time:start'];
                const gameWithTime = gameWithTimeRoutes.includes(this.$route.name);
                if (this.isMultiplicationAndDivision) {
                    if (this.unifiedGameDifficult === 1) {
                        if (this.parameters.fractions.left || this.parameters.fractions.right) {
                            return (
                                (!gameWithTime && 'abacus__field_open-four-spokes_game-without-time') ||
                                (gameWithTime && 'abacus__field_open-four-spokes_game-with-time')
                            );
                        }
                        return (
                            (!gameWithTime && 'abacus__field_open-two-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-two-spokes_game-with-time')
                        );
                    }
                    if (this.unifiedGameDifficult === 2) {
                        if (this.parameters.fractions.left || this.parameters.fractions.right) {
                            return (
                                (!gameWithTime && 'abacus__field_open-six-spokes_game-without-time') ||
                                (gameWithTime && 'abacus__field_open-six-spokes_game-with-time')
                            );
                        }
                        return (
                            (!gameWithTime && 'abacus__field_open-four-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-four-spokes_game-with-time')
                        );
                    }
                    if ([6, 7, 8].includes(this.unifiedGameDifficult)) {
                        return (
                            (!gameWithTime && 'abacus__field_open-eight-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-eight-spokes_game-with-time')
                        );
                    }
                    const digitCapacities = flatten(Object.values(this.digitCapacity));
                    if (
                        (digitCapacities[0] === 2 && digitCapacities[1] === 3) ||
                        (digitCapacities[0] === 3 && digitCapacities[1] === 2)
                    ) {
                        return (
                            (!gameWithTime && 'abacus__field_open-six-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-six-spokes_game-with-time')
                        );
                    }
                }
                if (this.isExpressions) {
                    if (this.gameDifficult === 'Однозначные цифры' || this.gameDifficult === 1) {
                        return (
                            (!gameWithTime && 'abacus__field_open-three-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-three-spokes_game-with-time')
                        );
                    }
                    if (this.gameDifficult === 'Двузначные цифры' || this.gameDifficult === 2) {
                        return (
                            (!gameWithTime && 'abacus__field_open-four-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-four-spokes_game-with-time')
                        );
                    }
                    if (this.gameDifficult === 'Трехзначные цифры' || this.gameDifficult === 3) {
                        return (
                            (!gameWithTime && 'abacus__field_open-five-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-five-spokes_game-with-time')
                        );
                    }
                    if (this.gameDifficult === 'Четырехзначные цифры' || this.gameDifficult === 4) {
                        return (
                            (!gameWithTime && 'abacus__field_open-six-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-six-spokes_game-with-time')
                        );
                    }
                    if (
                        this.gameDifficult === 'Пятизначные цифры' ||
                        this.gameDifficult === 5 ||
                        this.gameDifficult === 'Шестизначные цифры' ||
                        this.gameDifficult === 6 ||
                        this.gameDifficult === 'Семизначные цифры' ||
                        this.gameDifficult === 7 ||
                        this.gameDifficult === 'Восьмизначные цифры' ||
                        this.gameDifficult === 8
                    ) {
                        return (
                            (!gameWithTime && 'abacus__field_open-eight-spokes_game-without-time') ||
                            (gameWithTime && 'abacus__field_open-eight-spokes_game-with-time')
                        );
                    }
                }
                const difficultWithFractionalPart =
                    this.unifiedGameDifficult + (this.parameters.fractions ? this.parameters.fractionalPart : 0);
                if (difficultWithFractionalPart === 1) {
                    return (
                        (!gameWithTime && 'abacus__field_open-two-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-two-spokes_game-with-time')
                    );
                }
                if (difficultWithFractionalPart === 2) {
                    return (
                        (!gameWithTime && 'abacus__field_open-three-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-three-spokes_game-with-time')
                    );
                }
                if (difficultWithFractionalPart === 3) {
                    return (
                        (!gameWithTime && 'abacus__field_open-four-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-four-spokes_game-with-time')
                    );
                }
                if (difficultWithFractionalPart === 4) {
                    return (
                        (!gameWithTime && 'abacus__field_open-five-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-five-spokes_game-with-time')
                    );
                }
                if (difficultWithFractionalPart === 5) {
                    return (
                        (!gameWithTime && 'abacus__field_open-six-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-six-spokes_game-with-time')
                    );
                }
                if (difficultWithFractionalPart === 6) {
                    return (
                        (!gameWithTime && 'abacus__field_open-seven-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-seven-spokes_game-with-time')
                    );
                }
                if (difficultWithFractionalPart > 6) {
                    return (
                        (!gameWithTime && 'abacus__field_open-eight-spokes_game-without-time') ||
                        (gameWithTime && 'abacus__field_open-eight-spokes_game-with-time')
                    );
                }
                return '';
            },
            unifiedGameDifficult() {
                switch (this.gameDifficult) {
                    case 1:
                    case 'Однозначные цифры':
                        return 1;
                    case 2:
                    case 'Двузначные цифры':
                        return 2;
                    case 3:
                    case 'Трехзначные цифры':
                        return 3;
                    case 4:
                    case 'Четырехзначные цифры':
                        return 4;
                    case 5:
                    case 'Пятизначные цифры':
                        return 5;
                    case 6:
                    case 'Шестизначные цифры':
                        return 6;
                    case 7:
                    case 'Семизначные цифры':
                        return 7;
                    case 8:
                    case 'Восьмизначные цифры':
                        return 8;
                    default:
                        return 1;
                }
            },
            opponentInfoText() {
                return this.opponent && `${this.opponent.fullName} (${this.opponent.city})`;
            },
            tableCellsClass() {
                if (this.opponentTables[0].length === 4) {
                    return 'example-table__cell_big-table';
                } else if (this.opponentTables[0].length > 4) {
                    return 'example-table__cell_huge-table';
                }
                return '';
            },
            modeName() {
                if (this.mode) {
                    switch (this.mode) {
                        case 'На счетах':
                            return this.$i18n.t('trainer.trainer_gameFormSettingValue_onAbacus');
                        case 'В уме':
                            return this.$i18n.t('trainer.trainer_gameFormSettingValue_inMind');
                        case 'Любой':
                            return this.$i18n.t('trainer.trainer_gameFormSettingValue_any');
                    }
                }
                return '';
            },
            isMultiplicationAndDivision() {
                return this.$route.name.includes('multiplication-and-division');
            },
            isAddAndSub() {
                return this.$route.name.includes('addition-and-subtraction') || this.$route.name.includes('anzan');
            },
            isExpressions() {
                return this.$route.name.includes('expressions');
            },
        },
        components: {
            DescriptionButton,
            Description,
            Abacus,
            Tips,
            TipButton,
            SchulteAbacus,
            FlashCardsAbacus,
            MultiplayerMode,
        },
    };
</script>

<style lang="scss" scoped>
    .game-info {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        padding: 0 10px;
        &_top-centered {
            margin: 0 auto;
        }
    }

    .game-info__block {
        display: flex;
        flex-direction: column;
    }

    .game-info__item {
        width: 100px;
        height: 43px;
        border-radius: $border-radius-40;
        color: $x-color-white;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 10px;
        position: relative;
        @include media($sm) {
            width: 169px;
            height: 64px;
            margin-bottom: 20px;
        }
        @include media($lg) {
            width: 200px;
            height: 76px;
            border-radius: $border-radius-40;
        }
    }

    .game-info__scores {
        position: relative;
        background: $hsl-color-orange-64;
    }

    .game-info__scores--blue {
        background-color: $hsl-color-blue-56;
    }

    .left-score,
    .right-score {
        font-weight: 500;
        background: $x-color-white;
        color: $x-color-black;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        font-size: 12.5px;
        line-height: 15px;
        @include media($sm) {
            width: 45px;
            height: 45px;
            font-size: 18px;
            line-height: 22px;
        }
        @include media($lg) {
            font-size: 22px;
            line-height: 26px;
            width: 53px;
            height: 53px;
        }
    }

    .left-score::before {
        .playbox-score {
            content: '';
            position: absolute;
            background: url('../../../assets/images/trainer/goodAnswer.png') no-repeat;

            background-size: cover;
            top: 5px;
            left: 5px;
            width: 12px;
            height: 12px;
            @include media($sm) {
                top: 8px;
                left: 8px;
                width: 19px;
                height: 19px;
            }
            @include media($lg) {
                top: 10px;
                right: 10px;
                width: 22px;
                height: 22px;
            }
        }
    }

    .right-score::after {
        .playbox-score {
            content: '';
            position: absolute;
            background: url('../../../assets/images/trainer/badAnswer.png') no-repeat;
            background-size: cover;
            top: 5px;
            right: 5px;
            width: 12px;
            height: 12px;
            @include media($sm) {
                top: 8px;
                right: 8px;
                width: 19px;
                height: 19px;
            }
            @include media($lg) {
                top: 10px;
                right: 10px;
                width: 22px;
                height: 22px;
            }
        }
    }

    .colon {
        font-weight: 500;
        margin: 12px 11px;
        font-size: 18px;
        line-height: 21px;
        @include media($sm) {
            margin: 16px 26px;
            font-size: 27px;
            line-height: 32px;
        }
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
            margin: 20px 30px;
        }
    }

    .time__speed-up {
        background: #6bbd6c;
        height: 40px;
        width: 40px;
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        text-align: center;
        color: #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: -10px;
        right: 0;
        @media screen and (max-width: 575px) {
            height: 30px;
            width: 30px;
        }
        @media screen and (max-width: 360px) {
            height: 20px;
            width: 20px;
            font-size: 12px;
        }
    }

    .game-info__mode {
        width: 100px;
        height: 43px;
        border-radius: $border-radius-40;
        background: $x-color-white;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 10px;
        @include media($sm) {
            width: 169px;
            height: 64px;
            margin-bottom: 20px;
        }
        @include media($lg) {
            width: 200px;
            height: 76px;
            border-radius: $border-radius-40;
        }
        .game-mode__icon {
            color: #2187ff;
        }
        .mode__value {
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            color: #333333;
            margin-left: 3px;
            @media screen and (max-width: 991px) {
                font-size: 13px;
                line-height: 16px;
            }
            @media screen and (max-width: 767px) {
                font-size: 9px;
                line-height: 11px;
            }
        }
    }

    .game-info__time,
    .game-info__speed {
        background: $hsl-color-yellow-66;
        &_spaced {
            margin-right: 10px;
        }
    }

    .game-info__speed {
        color: $x-color-full-black;
    }

    .time__icon {
        width: 29px;
        height: 29px;
        @include media($sm) {
            width: 42px;
            height: 42px;
        }
        @include media($lg) {
            width: 50px;
            height: 50px;
        }
    }
    .time__value {
        font-weight: 500;
        color: $x-color-black;
        margin-left: 4.5px;
        min-width: 2.9em;
        font-size: 13px;
        line-height: 15px;
        @include media($sm) {
            font-size: 19px;
            line-height: 22px;
            margin-left: 7px;
        }
        @include media($lg) {
            font-size: 22px;
            line-height: 26px;
        }
    }

    .speed__icon {
        margin-right: 5px;
        font-size: 12px;
        @include media($sm) {
            font-size: 16px;
        }
    }

    .speed__value {
        font-size: 14px;
        @include media($sm) {
            font-size: 20px;
        }
    }

    .left-score-leave-active {
        color: $x-color-white;
        background: $hsl-color-green-58;
        transition: all 0.5s;
    }
    .left-score-leave-to {
        font-size: 26px !important;
        line-height: 31px !important;
        color: $x-color-white !important;
        background: $hsl-color-green-58 !important;
        font-weight: 500;
    }
    .left-score-enter-active {
        transition: all 0.7s;
    }
    .right-score-enter {
        font-size: 26px !important;
        line-height: 31px !important;
        color: $x-color-white !important;
        background: $hsl-color-orange-64 !important;
    }
    .right-score-enter-to {
        font-weight: normal;
    }
    .right-score-leave-active {
        color: $x-color-white;
        background: $hsl-color-orange-64;
        transition: all 0.5s;
    }
    .right-score-leave-to {
        font-size: 26px !important;
        line-height: 31px !important;
        color: $x-color-white !important;
        background: $hsl-color-orange-64 !important;
        font-weight: 500;
    }
    .right-score-enter-active {
        transition: all 0.7s;
    }
    .right-score-enter {
        font-size: 26px !important;
        line-height: 31px !important;
        color: $x-color-white !important;
        background: $hsl-color-orange-64 !important;
    }
    .right-score-enter-to {
        font-weight: normal;
    }

    .opponent {
        background: #ffffff;
        width: 100px;
        border: 1px solid #2187ff;
        box-sizing: border-box;
        border-radius: 10px;
        padding: 25px 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        @include media($sm) {
            width: 169px;
            padding: 25px 25px 35px 25px;
        }
        @include media($lg) {
            width: 200px;
        }
        .opponent__info {
            display: flex;
            justify-content: center;
            margin-bottom: 10px;
        }
        .opponent__name {
            font-size: 16px;
            line-height: 19px;
            color: #333333;
            @media screen and (max-width: 575px) {
                font-size: 10px;
                line-height: 16px;
            }
            @media screen and (max-width: 359px) {
                font-size: 8px;
                line-height: 9px;
            }
        }
        .opponent__avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            @media screen and (max-width: 575px) {
                width: 30px;
                height: 30px;
            }
            @media screen and (max-width: 359px) {
                width: 20px;
                height: 20px;
            }
        }
        .opponent__example_answering {
            font-style: normal;
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            text-align: center;
            color: #333333;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            span {
                margin: 30px 0 5px 0;
            }
            img {
                width: 76px;
                height: 52px;
            }
        }
        .opponent__example-number {
            background: #f7f7ff;
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            text-align: center;
            color: #2187ff;
            margin: 0 1px 1px 1px;
            padding: 2px 12px;
            &:last-child {
                border-radius: 0 0 4.5px 4.5px;
            }
            &:first-child {
                border-radius: 4.5px 4.5px 0 0;
            }
            @media screen and (max-width: 575px) {
                font-size: 13px;
                line-height: 16px;
            }
            @media screen and (max-width: 359px) {
                font-size: 8px;
                line-height: 9px;
            }
        }
        .opponent__example-divider {
            opacity: 0.6;
            border: 1px solid #2187ff;
        }
    }

    .opponent__tables-container {
        &:first-child {
            margin-bottom: 10px;
        }
    }

    .opponent__example-table {
        display: flex;
        background: #2187ff;
        opacity: 0.4;
        border: 1px solid #2187ff;
    }

    .example-table__cell {
        background: #ffffff;
        width: 52px;
        height: 52px;
        border-radius: 5px;
        border: 1px solid #2187ff;
        display: flex;
        align-items: center;
        justify-content: center;
        &_huge-table {
            width: 19px;
            height: 19px;
        }
        &_big-table {
            width: 30px;
            height: 30px;
        }
    }

    .cell-value {
        color: #2187ff;
        font-weight: normal;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        &_huge-table {
            font-size: 7px;
            line-height: 9px;
        }
    }

    .tip-button {
        margin-bottom: 10px;
        @include media($sm) {
            margin-bottom: 20px;
        }
    }

    .abacus-button {
        cursor: pointer;
        width: 100px;
        height: 43px;
        border-radius: $border-radius-40;
        background: $x-color-white;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 10px;
        @include media($sm) {
            width: 169px;
            height: 64px;
            margin-bottom: 20px;
        }
        @include media($lg) {
            width: 200px;
            height: 76px;
            border-radius: $border-radius-40;
        }
        .abacus__arrow-icon {
            color: #2187ff;
            transition: all 0.3s ease;
        }
        .abacus__arrow-icon--rotated {
            transform: scaleX(-1);
        }
        .abacus__icon {
            color: $hsl-color-green-58;
        }
        .abacus__value {
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            color: #333333;
            margin-left: 3px;
            @media screen and (max-width: 991px) {
                font-size: 13px;
                line-height: 16px;
            }
            @media screen and (max-width: 767px) {
                font-size: 9px;
                line-height: 11px;
            }
        }
    }
    .abacus__field {
        &_open-two-spokes_game-without-time,
        &_open-two-spokes_game-with-time {
            width: 185px;
            margin: auto;
            @media screen and (max-device-width: 1438px) {
                width: 115px;
            }
            @media screen and (max-device-width: 991px) {
                width: 77px;
                height: 200px;
            }
            @media screen and (max-device-width: 767px) {
                width: 65px;
                height: 200px;
            }
            @media screen and (max-device-width: 575px) {
                width: 52px;
                height: 180px;
            }
            @media screen and (max-device-width: 359px) {
                width: 40px;
                height: 145px;
            }
        }

        &_open-three-spokes_game-without-time,
        &_open-three-spokes_game-with-time {
            width: 275px;
            margin: auto;
            @media screen and (max-device-width: 1438px) {
                width: 165px;
            }
            @media screen and (max-device-width: 767px) {
                width: 100px;
                height: 200px;
            }
            @media screen and (max-device-width: 575px) {
                width: 80px;
                height: 190px;
            }
        }
        &_open-three-spokes_game-without-time {
            @media screen and (min-device-width: 768px) and (max-device-width: 991px) {
                width: 115px;
                height: 200px;
            }
            @media screen and (max-device-width: 359px) {
                width: 60px;
                height: 145px;
            }
        }
        &_open-three-spokes_game-with-time {
            @media screen and (min-device-width: 768px) and (max-device-width: 991px) {
                width: 110px;
                height: 200px;
            }
            @media screen and (max-device-width: 359px) {
                width: 55px;
                height: 145px;
            }
        }

        &_open-four-spokes_game-without-time,
        &_open-four-spokes_game-with-time {
            width: 365px;
            margin: auto;
            @media screen and (max-device-width: 1438px) {
                width: 225px;
            }
            @media screen and (max-device-width: 767px) {
                width: 130px;
                height: 200px;
            }
            @media screen and (max-device-width: 575px) {
                width: 105px;
                height: 190px;
            }
            @media screen and (max-device-width: 359px) {
                width: 75px;
            }
        }
        &_open-four-spokes_game-without-time {
            @media screen and (min-device-width: 768px) and (max-device-width: 991px) {
                width: 155px;
                height: 200px;
            }
        }
        &_open-four-spokes_game-with-time {
            @media screen and (min-device-width: 768px) and (max-device-width: 991px) {
                width: 145px;
                height: 200px;
            }
        }

        &_open-five-spokes_game-without-time,
        &_open-five-spokes_game-with-time {
            width: 460px;
            @media screen and (max-device-width: 1438px) {
                width: 285px;
            }
            @media screen and (max-device-width: 991px) {
                width: 190px;
                height: 200px;
            }
            @media screen and (max-device-width: 767px) {
                width: 160px;
                height: 200px;
            }
            @media screen and (max-device-width: 575px) {
                width: 130px;
                height: 190px;
            }
            @media screen and (max-device-width: 359px) {
                width: 95px;
                height: 145px;
            }
        }

        &_open-six-spokes_game-without-time,
        &_open-six-spokes_game-with-time {
            width: 560px;
            @media screen and (max-device-width: 1438px) {
                width: 340px;
            }
            @media screen and (max-device-width: 991px) {
                width: 215px;
                height: 200px;
            }
            @media screen and (max-device-width: 767px) {
                width: 192px;
                height: 200px;
            }
            @media screen and (max-device-width: 575px) {
                width: 156px;
                height: 190px;
            }
            @media screen and (max-device-width: 359px) {
                width: 113px;
                height: 145px;
            }
        }

        &_open-eight-spokes_game-without-time,
        &_open-eight-spokes_game-with-time {
            width: 735px;
            @media screen and (max-device-width: 1438px) {
                width: 450px;
            }
            @media screen and (max-device-width: 991px) {
                width: 286px;
                height: 200px;
            }
            @media screen and (max-device-width: 767px) {
                width: 256px;
                height: 200px;
            }
            @media screen and (max-device-width: 575px) {
                width: 208px;
                height: 190px;
            }
            @media screen and (max-device-width: 359px) {
                width: 153px;
                height: 145px;
            }
        }
    }

    .opponent__example-abacus {
        height: 140px;
        opacity: 0.4;
    }
</style>
